import {calculator} from "@/services/calculator.ts";
import {useAtom} from "jotai/index";
import {calculator_limits, calculator_product, selected_calculator_type} from "@/atoms/common.ts";
import Factories from "@/utils/factories.ts";
import CalculatorLimitTypes from "@/types_and_interfaces/calculator_limit_types.ts";
import CalculatorLimit from "@/types_and_interfaces/calculator_limit.ts";
import CommonClass from "@/utils/common.ts";
import ProductInterface from "@/types_and_interfaces/product_interface.ts";
import eventEmitter from "@/utils/event_emmiter_instance.ts";
import {useTranslation} from "react-i18next";
import {profile} from "@/atoms/profile.ts";
import {country_code} from "@/utils/constants.ts";


function useCalc() {
    const [calc_types, set_calc_types ] = useAtom(calculator_limits)
    const [selected_type, set_selected_type ] = useAtom(selected_calculator_type)
    const [product, set_product ] = useAtom<ProductInterface | undefined | {}>(calculator_product)
    const [user ] = useAtom(profile)
    const {t} = useTranslation()
    
    
    const get_types = () => {
        return calc_types
    }
    
    const get_product = () => {
        return product
    }
    
    const get_selected_type = () => {
        return selected_type
    }
    
    const product_handler = (x:ProductInterface | {} | undefined) => {
        set_product(x)
    }
    
    const type_handle = async (x?:CalculatorLimit, months?:number, amount?:number) => {
        
        if(months && amount) {
            update_product(months, amount)
            return
        }
        
        if(x) {
            product_handler(undefined)
            set_selected_type(x)
            update_product(x.default_month, x.default_capital, x)
        }
    }
    
    const update_product = async ( months:number, capital:number, type?:CalculatorLimit) => {
        const current_type = type ? type : selected_type
        if(
            (current_type as CalculatorLimit).min_amount > capital ||
            (current_type as CalculatorLimit).max_amount < capital ||
            (current_type as CalculatorLimit).min_installments > months ||
            (current_type as CalculatorLimit).max_installments < months
        ) {
            product_handler({})
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "warning", t("calculator_incorect_values"))
            return
        }
        
        let apply_form_body = sessionStorage.getItem(CommonClass.local_storage_fields.apply_credit_form_body)
        let parsed = JSON.parse(apply_form_body || "{}")
        let res
        
        
        if(
            apply_form_body &&
            Object.keys(parsed).length
        ) {
            try {
                res = await calculator().get_products(parsed?.period, parsed?.amn,  (current_type as CalculatorLimit)?.category_id)
            } catch (e) {
                res = await calculator().get_products(String(months), String(capital), (current_type as CalculatorLimit)?.category_id)
            }
            
            if(typeof user?.phone === "string" && parsed?.number === CommonClass.formatNumber(user?.phone?.replace(country_code, ""))) {
                sessionStorage.removeItem(CommonClass.local_storage_fields.apply_credit_form_body)
            }
            
        } else {
            res = await calculator().get_products(String(months), String(capital), (current_type as CalculatorLimit)?.category_id)
        }
        
        try {
            if(res.status === 200 && Object.keys(res.response?.exactMatch).length) {
                let obj = Factories.product_factory(res.response?.exactMatch)
                
                set_product(obj)
                
                let session_data = {
                    amn: obj.capital,
                    type: (current_type as CalculatorLimit)?.category_name,
                    period: obj.months,
                }
                
                localStorage.setItem(CommonClass.local_storage_fields.session_calculator_values, JSON.stringify(session_data))
                
            } else {
                set_product({})
            }

        } catch (e){
            set_product({})
        }
    }
    
    const sorted_types = () => {
        const keys: (keyof CalculatorLimitTypes)[] = ['auto', 'refinance', 'personal'];
        const types = get_types(); // assuming this returns CalculatorLimitTypes | {} | undefined
        
        // Type guard to check if `types` is an object with properties of `CalculatorLimitTypes`
        if (types && typeof types === "object" && Object.keys(types).length > 0) {
            // Assert that `types` is `CalculatorLimitTypes`
            const typedTypes = types as CalculatorLimitTypes;
            
            // Filter and sort the types based on `order_number`
            const res = keys
                .filter((k) => typedTypes[k] !== undefined)
                .map((k) => typedTypes[k])
                .sort((a, b) => a.order_number - b.order_number);
            
            return res;
        } else {
            return [];
        }
    }

    const update_calculator_limits = async ()=> {
        const res = await calculator().get_limits()
        
        if(res.status === 200 && Object.keys(res.response?.data).length) {
            let obj:CalculatorLimitTypes = Factories.calculator_limits_factory(res.response?.data)
            
            
            if(typeof obj === "object" &&  Object.keys(obj).length) {
                let session_values = localStorage.getItem(CommonClass.local_storage_fields.session_calculator_values)
                let parsed = JSON.parse(session_values || "{}")
                
                const generate_default = (): CalculatorLimit => {
                    const keys: (keyof CalculatorLimitTypes)[] = ['auto', 'refinance', 'personal'];
                    let res = undefined
                    
                    keys.forEach((key) => {
                        const current: CalculatorLimit = obj[key];
                        
                        if(current?.order_number === 1) res = current
                    });
                    
                    if(!res) res = obj.personal
                    
                    return res
                }

                set_calc_types(obj)
                
                
                if(
                    session_values &&
                    Object.keys(parsed).length
                ) {

                    let session_obj = {
                        ...obj[parsed.type as keyof CalculatorLimitTypes],
                        ...{
                            default_capital: parsed.amn,
                            default_month: parsed?.period
                        }
                    }
                    
                    set_selected_type(session_obj)
                    await update_product(parsed?.period, parsed.amn, obj[parsed.type as keyof CalculatorLimitTypes])
                    
                } else {
                    const selected_type = generate_default()
                    set_selected_type(selected_type)
                    await update_product(selected_type.default_month, selected_type.default_capital, selected_type)
                }
                
            } else {
                set_calc_types({})
                
            }
            
        } else {
            set_calc_types({})
        }
    }
    
    
    return {
        update_calculator_limits,
        get_types,
        get_selected_type,
        type_handle,
        update_product,
        get_product,
        product_handler,
        sorted_types
    }
}

export default useCalc